import eventbus from 'tiny-emitter/instance';
import { SEAT, TYPE, SEATSTATUS, RENDERER_MODE, CHANNEL, EVENTSTATUS } from '@/constants';
import Model, { ModelParams } from '../model';
import { ConflictableMixin } from '../../mixins/ConflictableMixin';
import { SelectableMixin } from '../../mixins/SelectableMixin';
import { LockableMixin } from '../../mixins/LockableMixin';
import { GroupableAndDisabledMixin } from '../../mixins/GroupableAndDisabledMixin';
import { PosMixin } from '../../mixins/PosMixin';
import { CategoryMixin } from '../../mixins/CategoryMixin';
import { ReservableMixin } from '../../mixins/ReservableMixin';
import { CartableMixin } from '../../mixins/CartableMixin';
import { OccupiedMixin } from '../../mixins/OccupiedMixin';
import { SomeRequired } from '@/core/typeHelper';
import { Table } from './table';
import SVG from 'svg.js';

export class TableSeat extends OccupiedMixin(
  CartableMixin(
    ReservableMixin(
      PosMixin(GroupableAndDisabledMixin(LockableMixin(ConflictableMixin(CategoryMixin(SelectableMixin(Model))))))
    )
  )
) {
  table: Table;
  declare label: SVG.Text;
  declare shape: SVG.Circle | SVG.Rect;

  constructor(params: SomeRequired<ModelParams, 'table'>) {
    super(params);
    this.table = params.table;
    this.initView();
  }

  get type() {
    return TYPE.TABLESEAT;
  }

  // @ts-expect-error
  get sid() {
    return this.table.sid;
  }

  get seatCanBeSelectedOrDeselected() {
    if (RENDERER_MODE) {
      if (this.table.attributes.isGrouped) {
        return this.table.seatCanBeSelectedOrDeselected;
      } else if (
        this.attributes.status === SEATSTATUS.OCCUPIED ||
        this.attributes.status === SEATSTATUS.RESERVED ||
        this.attributes.status === SEATSTATUS.DISABLED ||
        this.inCart
      ) {
        return false;
      } else if (this.locked) {
        return false;
      }
    } else if (!RENDERER_MODE && this.app.props.status !== EVENTSTATUS.DRAFT) {
      if (
        this.attributes.status === SEATSTATUS.FREE ||
        this.attributes.status === SEATSTATUS.LOCKED ||
        this.attributes.status === SEATSTATUS.DISABLED
      ) {
        return true;
      } else {
        return false;
      }
    }
    return true;
  }

  assignCategory(categoryId: number) {
    this.categoryId = categoryId;
  }

  unassignCategory() {
    this.categoryId = null;
  }

  createLabel(x = 0, y = 0) {
    return this.canvas
      .plain(this.attributes.label)
      .font({ family: 'Roboto', style: 'normal' })
      .attr({
        x,
        y,
        'text-anchor': 'middle',
        'dominant-baseline': 'central',
      })
      .addClass('label table-seat-label');
  }

  changeLabel(label: string = SEAT.LABEL) {
    if (this.app.props.status === EVENTSTATUS.PUBLISHED) {
      return;
    } else if (this.app.props.status === EVENTSTATUS.PAUSED) {
      if (this.attributes.status !== SEATSTATUS.FREE) {
        return;
      }
    }
    label = label.trim();
    this.attributes.label = label;
    this.label.plain(this.attributes.label);
    this.scaleLabel();
  }

  scaleLabel(size = 10) {
    this.label.font({ size: size });
    let width = (this.label.node as unknown as SVGTextElement).textLength.baseVal.value;
    //firefox fix.
    if (width <= 0) {
      width = this.label.bbox().width;
      width -= 4;
    }
    const standardSize = size;
    const optimizedFontSizeFactor = SEAT.INNER_RADIUS / (width + 6);
    const optimizedFontSize = standardSize * optimizedFontSizeFactor;
    let fontSize = standardSize;
    if (optimizedFontSizeFactor <= 1 && optimizedFontSize <= standardSize) {
      fontSize = optimizedFontSize;
    }
    this.label.font({ size: fontSize });
  }

  clearLabel() {
    this.changeLabel();
  }

  showLabel() {
    this.label.show();
  }

  hideLabel() {
    this.label.hide();
  }

  initView() {
    this.view = this.canvas.group().addClass('table-seat');
    this.shape = this.view.circle(SEAT.INNER_RADIUS * this.app.seatmap.scale).addClass('table-seat-shape');

    if (!RENDERER_MODE) {
      //DESIGNER
      this.label = this.createLabel();
      this.scaleLabel();
      this.view.add(this.label);
      this.label.front();
      if (this.categoryId) {
        //CategoryMixin
        this.applyCategoryColor(this.app.getCategoryColor(this.categoryId));
      }

      if (!this.pointOfSale) {
        // POSMixin
        this.pointOfSale = this.app.getPosObj(this.attributes.posId);
      }

      if (this.app.isShowPosColor()) {
        this.applyPosColor(this.app.getPosColor(this.pointOfSale));
      }

      if (this.inCart) {
        this.shape.addClass('cart');
      } else if (this.attributes.status === SEATSTATUS.RESERVED) {
        this.shape.addClass('reserved');
      } else if (this.attributes.status === SEATSTATUS.DISABLED) {
        this.shape.addClass('disabled');
      } else if (this.attributes.status !== SEATSTATUS.FREE && this.attributes.status !== SEATSTATUS.LOCKED) {
        this.shape.addClass('booked');
      }
    } else {
      //RENDERER
      const lthis = this;

      if (this.attributes.status !== SEATSTATUS.DISABLED) {
        this.shape.mouseover(function () {
          eventbus.emit('tooltip:show', lthis);
        });

        this.shape.mouseout(function () {
          eventbus.emit('tooltip:hide', lthis);
          eventbus.emit('hide:alert');
        });
      }

      const map = {
        [SEATSTATUS.FREE]: 'free-seat',
        [SEATSTATUS.LOCKED]: 'locked-seat',
        [SEATSTATUS.OCCUPIED]: 'occupied-seat',
        [SEATSTATUS.RESERVED]: 'reserved-seat',
        [SEATSTATUS.CART]: 'occupied-seat',
        [SEATSTATUS.DISABLED]: 'disabled-seat',
        [SEATSTATUS.SELECTED]: 'selected-seat',
      };

      // cart is not a state anymore
      if (this.inCart) {
        this.view.addClass('cart-seat');
      } else {
        this.view.addClass(map[this.attributes.status]);
      }

      //seats in user's own session
      if (this.inCart) {
        this.shape.addClass('cart');
      } else if (this.attributes.status === SEATSTATUS.DISABLED) {
        this.shape.addClass('disabled');
      } else if (this.attributes.status !== SEATSTATUS.FREE && this.attributes.status !== SEATSTATUS.LOCKED) {
        this.shape.addClass('booked');
      }
      //POS channel
      if (this.app.props.posID !== null && this.app.props.channel === CHANNEL.POS) {
        if (this.attributes.status === SEATSTATUS.RESERVED) {
          this.shape.addClass('reserved');
        }
        if (this.app.posFilterationFail(this)) {
          this.view.addClass('other-pos');
        } else {
          this.view.addClass('current-pos');
        }
      }
      if (this.app.props.channel === CHANNEL.REPORTING) {
        if (this.attributes.status === SEATSTATUS.RESERVED) {
          this.shape.addClass('reserved');
        }
      }

      const color = this.categoryId ? this.app.getCategoryColor(this.categoryId) : '#fff';

      this.view.style({ color });

      if (!this.app.hasActiveCategory() || this.app.isActiveCategory(this.categoryId)) {
        this.view.addClass('active-category');
      }

      // if (this.app.categoryFilterationFail(this)) {
      //   this.shape.addClass('other-category');
      // }
      //for POS channel
      this.view.addClass(`${this.app.props.channel}-mode`);
    }
  }

  redraw() {
    const [x, y] = this.attributes.position;

    this.shape.radius((SEAT.INNER_RADIUS / 2) * this.app.seatmap.scale).center(x, y);
    this.label?.font({ size: 10 * this.app.seatmap.scale });

    if (!RENDERER_MODE) {
      //DESIGNER
      this.label.attr({ x, y });
      this.app.showLabels ? this.showLabel() : this.hideLabel();
    }
    if (this.locked) {
      //LockableMixin
      this.initLockShape();
    }
    if (this.grouped) {
      //GroupableMixin
      this.initGroupedShape();
    }

    if (this.disabled) {
      //DisabledMixin
      this.initDisabledShape();
    }
    if (this.reserved) {
      this.initReservedShape();
    }
    if (this.inCart) {
      this.initCartShape();
    }
    if (this.occupied) {
      //this.initOccupiedShape();
    }
  }

  toJSON(keepLabel = true) {
    const { uuid, type } = this;
    const attributes = Object.assign({}, this.attributes);
    attributes.label = attributes.label === SEAT.LABEL ? '' : attributes.label;
    if (!keepLabel) delete attributes.label;
    return {
      uuid,
      type,
      attributes,
    };
  }

  destroy() {
    if (
      (this.app.props.status === EVENTSTATUS.PUBLISHED || this.app.props.status === EVENTSTATUS.PAUSED) &&
      this.attributes.status !== SEATSTATUS.FREE &&
      this.attributes.status !== SEATSTATUS.DISABLED
    ) {
      return;
    }
    this.view.remove();
  }

  changeMeta1(value: string) {
    this.setAttribute('meta1', value);
  }

  changeMeta2(value: string) {
    this.setAttribute('meta2', value);
  }

  static clone(seatParams: SomeRequired<ModelParams, 'type' | 'table'>, options: { ts?: number } = {}) {
    seatParams.uuid = Model.getUniqueIdStatic(seatParams.type);
    const groupId = seatParams.attributes._groupId;
    if (groupId) {
      const t = options.ts !== undefined ? options.ts : '';
      seatParams.attributes._groupId = seatParams.attributes._groupId + '_' + t;
    }
    return TableSeat.fromJSON(seatParams);
  }

  static fromJSON(params: SomeRequired<ModelParams, 'table'>) {
    params.attributes.label =
      params.attributes.label !== undefined && params.attributes.label !== '' ? params.attributes.label : SEAT.LABEL;
    return TableSeat.create(params);
  }

  static create(params: SomeRequired<ModelParams, 'table'>) {
    const attributes = {
      label: SEAT.LABEL,
      orderid: 0,
      position: [0, 0],
      status: SEATSTATUS.FREE,
    };
    params.attributes = Object.assign({}, attributes, params.attributes);
    return new TableSeat(params);
  }
}
